import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Reveal, MaskedLines } from "@/components/Reveal";

const GENEVA_IMG =
  "https://images.unsplash.com/photo-1726334574781-19ab11a2ea78?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxnZW5ldmElMjBhcmNoaXRlY3R1cmUlMjBsdXh1cnl8ZW58MHx8fHwxNzkyMjc1NjI5fDA&ixlib=rb-4.1.0&q=85";
const ARCH_IMG =
  "https://images.unsplash.com/photo-1670003112050-710fbf757482?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwyfHxnZW5ldmElMjBhcmNoaXRlY3R1cmUlMjBsdXh1cnl8ZW58MHx8fHwxNzkyMjc1NjI5fDA&ixlib=rb-4.1.0&q=85";

const SectionHead = ({ overline, titleA, titleB }) => (
  <Reveal>
    <p className="overline-label">{overline}</p>
    <h2 className="mt-4 max-w-3xl font-display text-2xl font-normal leading-[1.18] tracking-tight text-chalk sm:text-3xl">
      {titleA}
      <span className="font-serif italic text-brass">{titleB}</span>
    </h2>
  </Reveal>
);

const Chips = ({ items, testidPrefix }) => (
  <ul className="mt-8 flex flex-wrap gap-2">
    {items.map((it) => (
      <li
        key={it}
        data-testid={`${testidPrefix}-${it.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        className="border border-white/[0.1] px-3.5 py-1.5 font-mono text-[11px] tracking-[0.08em] text-mist/75 transition-colors duration-200 hover:border-brass/40 hover:text-chalk"
      >
        {it}
      </li>
    ))}
  </ul>
);

export default function Investissements() {
  const { t } = useTranslation("investissements");
  const stats = t("hero.stats", { returnObjects: true });
  const tags = t("hero.tags", { returnObjects: true });
  const startupsList = t("geo.startupsList", { returnObjects: true });
  const immoList = t("geo.immoList", { returnObjects: true });
  const immoBlocks = t("immo.blocks", { returnObjects: true });
  const mini = t("immo.mini", { returnObjects: true });
  const processSteps = t("process.steps", { returnObjects: true });
  const products = t("lending.products", { returnObjects: true });
  const industries = t("industries.items", { returnObjects: true });
  const healthItems = t("health.items", { returnObjects: true });
  const trio = t("trio", { returnObjects: true });

  return (
    <div data-testid="investissements-page" className="pt-32 lg:pt-40">
      <header className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
        <p className="overline-label mb-8 flex items-center gap-3">
          <span className="inline-block h-px w-10 bg-brass/60" />
          {t("hero.overline")}
        </p>
        <h1 className="max-w-4xl font-display text-4xl font-light leading-[1.08] tracking-tight text-chalk sm:text-5xl lg:text-6xl">
          <MaskedLines
            lines={[
              t("hero.titleA"),
              <span key="b" className="font-serif italic text-brass">{t("hero.titleB")}</span>,
            ]}
          />
        </h1>
        <p className="mt-8 max-w-2xl text-base leading-relaxed text-mist/85">{t("hero.lead")}</p>

        <div className="mt-12 grid grid-cols-2 gap-px bg-white/[0.06] lg:grid-cols-4">
          {stats.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.06} className="bg-ink">
              <div className="p-6 lg:p-8" data-testid={`invest-stat-${i + 1}`}>
                <p className="font-display text-3xl text-chalk">{s.value}</p>
                <p className="mt-2 text-xs leading-relaxed text-slate2">{s.label}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2}>
          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2">
            {tags.map((tag) => (
              <span key={tag} className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate2">
                <span className="h-1.5 w-1.5 rotate-45 bg-brass/60" />
                {tag}
              </span>
            ))}
          </div>
          <Link
            to="/investissements/portfolio"
            data-testid="invest-portfolio-link"
            className="mt-10 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-brass transition-colors duration-200 hover:text-chalk"
          >
            {t("hero.portfolioLink")} <ArrowUpRight size={14} />
          </Link>
        </Reveal>
      </header>

      <section className="mx-auto mt-24 max-w-7xl px-6 sm:px-8 lg:px-12 lg:mt-32">
        <SectionHead overline={t("geo.overline")} titleA={t("geo.titleA")} titleB={t("geo.titleB")} />
        <Reveal>
          <p className="mt-6 max-w-2xl text-sm leading-relaxed text-slate2">{t("geo.lead")}</p>
        </Reveal>
        <div className="mt-12 grid gap-8 lg:grid-cols-12">
          <Reveal className="lg:col-span-4">
            <div className="flex h-full flex-col justify-between border border-brass/25 bg-elevated/40 p-8">
              <div>
                <p className="overline-label">{t("geo.minTicketLabel")}</p>
                <p className="mt-6 font-display text-5xl text-chalk">
                  1 M<span className="ml-2 font-mono text-sm uppercase tracking-[0.2em] text-brass">CHF</span>
                </p>
              </div>
              <p className="mt-8 text-xs leading-relaxed text-slate2">{t("geo.minTicketNote")}</p>
            </div>
          </Reveal>
          <Reveal delay={0.08} className="lg:col-span-4">
            <div className="h-full border border-white/[0.08] bg-surface/40 p-8">
              <h3 className="font-display text-xl text-chalk">{t("geo.startupsTitle")}</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate2">{t("geo.startupsDesc")}</p>
              <Chips items={startupsList} testidPrefix="geo-startup" />
            </div>
          </Reveal>
          <Reveal delay={0.16} className="lg:col-span-4">
            <div className="h-full border border-white/[0.08] bg-surface/40 p-8">
              <h3 className="font-display text-xl text-chalk">{t("geo.immoTitle")}</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate2">{t("geo.immoDesc")}</p>
              <Chips items={immoList} testidPrefix="geo-immo" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/[0.06] bg-surface/30">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 py-24 sm:px-8 lg:grid-cols-12 lg:px-12 lg:py-32">
          <Reveal className="lg:col-span-5">
            <div className="overflow-hidden border border-white/10">
              <img
                src={GENEVA_IMG}
                alt={t("approach.imgAlt")}
                className="h-72 w-full object-cover transition-transform duration-700 hover:scale-105 lg:h-[420px]"
              />
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-7">
            <p className="overline-label">{t("approach.overline")}</p>
            <h2 className="mt-4 font-display text-2xl font-normal leading-[1.18] tracking-tight text-chalk sm:text-3xl">
              {t("approach.titleA")}
              <span className="font-serif italic text-brass">{t("approach.titleB")}</span>
            </h2>
            <div className="mt-6 space-y-5 text-sm leading-relaxed text-mist/85">
              <p>{t("approach.para1")}</p>
              <p className="text-slate2">{t("approach.para2")}</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-12 lg:py-32">
        <SectionHead overline={t("immo.overline")} titleA={t("immo.titleA")} titleB={t("immo.titleB")} />
        <div className="mt-12 grid gap-8 lg:grid-cols-2">
          {immoBlocks.map((b, i) => (
            <Reveal key={b.id} delay={i * 0.08}>
              <article
                data-testid={`immo-block-${b.id}`}
                className="h-full border border-white/[0.08] bg-ink p-8 transition-colors duration-300 hover:border-brass/35 lg:p-10"
              >
                <h3 className="font-display text-xl text-chalk">{b.title}</h3>
                <p className="mt-4 text-sm leading-relaxed text-mist/80">{b.description}</p>
                <ul className="mt-8 space-y-3 border-t border-white/[0.08] pt-6">
                  {b.bullets.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm text-mist/80">
                      <Check size={15} className="mt-0.5 shrink-0 text-brass" />
                      {item}
                    </li>
                  ))}
                </ul>
              </article>
            </Reveal>
          ))}
        </div>
        <div className="mt-8 grid gap-8 lg:grid-cols-2">
          <Reveal>
            <article className="h-full border border-white/[0.08] bg-ink p-8 lg:p-10">
              <h3 className="font-display text-xl text-chalk">{t("immo.marketingTitle")}</h3>
              <p className="mt-4 text-sm leading-relaxed text-mist/80">{t("immo.marketingDesc")}</p>
            </article>
          </Reveal>
          <Reveal delay={0.08}>
            <article className="h-full border border-white/[0.08] bg-ink p-8 lg:p-10">
              <h3 className="font-display text-xl text-chalk">{t("immo.salesTitle")}</h3>
              <p className="mt-4 text-sm leading-relaxed text-mist/80">{t("immo.salesDesc")}</p>
            </article>
          </Reveal>
        </div>
        <div className="mt-8 grid gap-px bg-white/[0.06] sm:grid-cols-3">
          {mini.map((m, i) => (
            <Reveal key={m.title} delay={i * 0.06} className="bg-surface/60">
              <div className="p-6">
                <p className="font-display text-base text-chalk">{m.title}</p>
                <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.18em] text-brass/70">{m.sub}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-t border-white/[0.06] bg-surface/30">
        <div className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-12 lg:py-32">
          <SectionHead overline={t("process.overline")} titleA={t("process.titleA")} titleB={t("process.titleB")} />
          <div className="mt-12 grid gap-px bg-white/[0.06] sm:grid-cols-2 lg:grid-cols-4">
            {processSteps.map((p, i) => (
              <Reveal key={p.num} delay={i * 0.06} className="bg-ink">
                <div data-testid={`process-step-${i + 1}`} className="h-full p-8 transition-colors duration-300 hover:bg-elevated">
                  <p className="font-mono text-xs tracking-[0.24em] text-brass/70">{p.num}</p>
                  <h3 className="mt-5 font-display text-lg text-chalk">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate2">{p.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-12 lg:py-32">
        <div className="grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <p className="overline-label">{t("lending.overline")}</p>
            <h2 className="mt-4 font-display text-2xl font-normal leading-[1.18] tracking-tight text-chalk sm:text-3xl">
              {t("lending.titleA")}
              <span className="font-serif italic text-brass">{t("lending.titleB")}</span>
            </h2>
            <div className="mt-6 space-y-5 text-sm leading-relaxed text-mist/85">
              <p>{t("lending.para1")}</p>
              <p className="text-slate2">{t("lending.para2")}</p>
            </div>
            <ul className="mt-8 grid gap-3 border-t border-white/[0.08] pt-8 sm:grid-cols-2">
              {products.map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm text-mist/80">
                  <Check size={15} className="mt-0.5 shrink-0 text-brass" />
                  {f}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.12} className="lg:col-span-5">
            <div className="flex h-full flex-col justify-between border border-white/[0.08] bg-surface/40 p-8 lg:p-10">
              <div>
                <p className="overline-label">{t("peCard.overline")}</p>
                <h3 className="mt-4 font-display text-xl text-chalk">{t("peCard.title")}</h3>
                <p className="mt-4 text-sm leading-relaxed text-slate2">{t("peCard.text")}</p>
              </div>
              <p className="mt-8 border-t border-white/[0.08] pt-6 font-serif text-base italic text-mist/80">
                {t("peCard.quote")}
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/[0.06] bg-surface/30">
        <div className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-12 lg:py-32">
          <div className="grid gap-12 lg:grid-cols-12">
            <Reveal className="lg:col-span-5">
              <p className="overline-label">{t("industries.overline")}</p>
              <h2 className="mt-4 font-display text-2xl font-normal leading-[1.18] tracking-tight text-chalk sm:text-3xl">
                {t("industries.titleA")}
                <span className="font-serif italic text-brass">{t("industries.titleB")}</span>
              </h2>
              <p className="mt-6 text-sm leading-relaxed text-slate2">{t("industries.lead")}</p>
            </Reveal>
            <Reveal delay={0.1} className="lg:col-span-7">
              <Chips items={industries} testidPrefix="industry" />
            </Reveal>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-12 lg:py-32">
        <div className="grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <p className="overline-label">{t("health.overline")}</p>
            <h2 className="mt-4 font-display text-2xl font-normal leading-[1.18] tracking-tight text-chalk sm:text-3xl">
              {t("health.titleA")}
              <span className="font-serif italic text-brass">{t("health.titleB")}</span>
            </h2>
            <p className="mt-6 text-sm leading-relaxed text-slate2">{t("health.lead")}</p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-7">
            <Chips items={healthItems} testidPrefix="health" />
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/[0.06] bg-surface/30">
        <div className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-12 lg:py-32">
          <div className="grid gap-10 lg:grid-cols-3">
            {trio.map((b, i) => (
              <Reveal key={b.overline} delay={i * 0.08}>
                <article className="h-full border border-white/[0.08] bg-ink p-8 transition-colors duration-300 hover:border-brass/35 lg:p-10">
                  <p className="overline-label">{b.overline}</p>
                  <h3 className="mt-4 font-display text-lg leading-snug text-chalk">{b.title}</h3>
                  <p className="mt-4 text-sm leading-relaxed text-slate2">{b.text}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-12 lg:py-32">
        <div className="grid items-center gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <p className="overline-label">{t("startups.overline")}</p>
            <h2 className="mt-4 font-display text-2xl font-normal leading-[1.18] tracking-tight text-chalk sm:text-3xl">
              {t("startups.titleA")}
              <span className="font-serif italic text-brass">{t("startups.titleB")}</span>
            </h2>
            <div className="mt-6 space-y-5 text-sm leading-relaxed text-mist/85">
              <p>{t("startups.para1")}</p>
              <p className="text-slate2">{t("startups.para2")}</p>
              <p className="text-slate2">{t("startups.para3")}</p>
            </div>
          </Reveal>
          <Reveal delay={0.12} className="lg:col-span-5">
            <div className="overflow-hidden border border-white/10">
              <img
                src={ARCH_IMG}
                alt={t("startups.imgAlt")}
                className="h-72 w-full object-cover transition-transform duration-700 hover:scale-105 lg:h-[400px]"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/[0.06] bg-surface/40">
        <div className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-12 lg:py-28">
          <Reveal className="max-w-3xl">
            <p className="overline-label">{t("final.overline")}</p>
            <h2 className="mt-4 font-display text-3xl font-light leading-[1.15] tracking-tight text-chalk sm:text-4xl">
              {t("final.titleA")}
              <span className="font-serif italic text-brass">{t("final.titleB")}</span>
            </h2>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-mist/85">{t("final.lead")}</p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/contact" data-testid="invest-final-cta-appointment" className="btn-brass">
                {t("final.btnPrimary")} <ArrowUpRight size={15} />
              </Link>
              <Link to="/conformite" data-testid="invest-final-cta-compliance" className="btn-ghost">
                {t("final.btnSecondary")}
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
